/**
 * @ngdoc service
 * @name userNotificationsApiService
 * @module flowingly.user.notifications
 *
 * @description A service that maintains a list of user notification items, for consumption by a component that displays them
 */

import { IUserNotificationDetail } from '@Shared.Angular/@types/core/contracts/queryModel/notifications/userNotificationDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { IUserNotificationsReadMessage } from '@Shared.Angular/@types/pubSub';
import { Services } from '@Shared.Angular/@types/services';
import angular from 'angular';

angular
  .module('flowingly.user.notifications')
  .factory('userNotificationsApiService', userNotificationsApiService);

userNotificationsApiService.$inject = [
  '$http',
  'APP_CONFIG',
  'sessionService',
  'pubsubService'
];

function userNotificationsApiService(
  $http: angular.IHttpService,
  APP_CONFIG: Services.APP_CONFIG,
  sessionService: Services.SessionService,
  pubsubService: Services.PubSubService
) {
  //API
  const service = {
    notificationCount: 0,
    dismissNotification: dismissNotification,
    getNotifications: getNotifications,
    getNotificationCount: getNotificationCount,
    readNotification: readNotification
  };
  const subscriberId = 'runner.user.notifications.api';

  pubsubService.subscribe(
    'SIGNALR_USER_NOTIFICATION_CREATED',
    () => {
      service.notificationCount++;
    },
    subscriberId
  );
  pubsubService.subscribe(
    'SIGNALR_USER_NOTIFICATIONS_READ',
    (event, message: IUserNotificationsReadMessage) => {
      service.notificationCount -= message.userNotificationIds.length;
    },
    subscriberId
  );
  pubsubService.subscribe(
    'SIGNALR_USER_NOTIFICATION_DISMISSED',
    () => {
      service.notificationCount--;
    },
    subscriberId
  );

  return service;

  /// Public API Methods /////////////////////////////////////////////////////////////

  function dismissNotification(notificationId, noSpinner) {
    return $http.delete<void>(
      APP_CONFIG.apiBaseUrl +
        'usernotifications/' +
        sessionService.getUser().id +
        '/notifications/' +
        notificationId,
      { noSpinner: noSpinner }
    );
  }

  function getNotificationCount(noSpinner = false) {
    return $http
      .get<number>(
        APP_CONFIG.apiBaseUrl +
          'usernotifications/' +
          sessionService.getUser().id +
          '/notificationCount',
        { noSpinner: noSpinner }
      )
      .then(function (response) {
        service.notificationCount = response.data as number;
      });
  }

  function getNotifications(pageNumber: number, noSpinner = false) {
    return $http
      .get<IUserNotificationDetail[]>(
        APP_CONFIG.apiBaseUrl +
          'usernotifications/' +
          sessionService.getUser().id +
          '/notifications?pageNumber=' +
          pageNumber,
        { noSpinner: noSpinner }
      )
      .then(function (response) {
        return response.data;
      });
  }

  function readNotification(flowId: Guid, noSpinner = false) {
    return $http.put(
      APP_CONFIG.apiBaseUrl +
        'usernotifications/' +
        sessionService.getUser().id +
        '/notifications/' +
        flowId,
      { noSpinner: noSpinner }
    );
  }
}

export type UserNotificationsApiServiceType = ReturnType<
  typeof userNotificationsApiService
>;
